import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReceiptIndianRupee, Loader2, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

export default function Setup() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [admin, setAdmin] = useState({ name: "", email: "", password: "" });
  const [settings, setSettings] = useState({
    trade_name: "", proprietor_name: "", business_pan: "",
    address1: "", city: "", state: "", pin: "",
  });

  useEffect(() => {
    api.get("/setup/status").then(({ data }) => {
      if (!data.needs_setup) navigate("/login");
    });
  }, [navigate]);

  const finish = async () => {
    setError("");
    setLoading(true);
    try {
      await api.post("/setup/init", { admin, settings });
      await refresh();
      toast.success("Setup complete. Welcome!");
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-9 h-9 rounded-md bg-[#FF4500] flex items-center justify-center">
            <ReceiptIndianRupee className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-semibold text-lg">Payslip Portal · First-run setup</span>
        </div>

        <div className="flex items-center gap-2 mb-8">
          {[1, 2].map((s) => (
            <div key={s} className={`flex-1 h-1 rounded-full ${step >= s ? "bg-[#FF4500]" : "bg-secondary"}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-5" data-testid="setup-step-admin">
            <div>
              <h1 className="font-display text-2xl font-semibold">Create the Admin account</h1>
              <p className="text-sm text-muted-foreground mt-1">This account manages the whole portal.</p>
            </div>
            <Field label="Full name"><Input value={admin.name} onChange={(e) => setAdmin({ ...admin, name: e.target.value })} data-testid="setup-admin-name" /></Field>
            <Field label="Email"><Input type="email" value={admin.email} onChange={(e) => setAdmin({ ...admin, email: e.target.value })} data-testid="setup-admin-email" /></Field>
            <Field label="Password"><Input type="password" value={admin.password} onChange={(e) => setAdmin({ ...admin, password: e.target.value })} placeholder="At least 8 characters" data-testid="setup-admin-password" /></Field>
            <Button className="w-full h-11" data-testid="setup-next-button"
              disabled={!admin.name || !admin.email || admin.password.length < 8}
              onClick={() => setStep(2)}>
              Continue <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5" data-testid="setup-step-company">
            <div>
              <h1 className="font-display text-2xl font-semibold">Company details</h1>
              <p className="text-sm text-muted-foreground mt-1">This appears on the payslip header. You can refine everything later in Settings.</p>
            </div>
            <Field label="Trade name (required)"><Input value={settings.trade_name} onChange={(e) => setSettings({ ...settings, trade_name: e.target.value })} data-testid="setup-trade-name" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Proprietor name"><Input value={settings.proprietor_name} onChange={(e) => setSettings({ ...settings, proprietor_name: e.target.value })} data-testid="setup-proprietor" /></Field>
              <Field label="Business PAN"><Input value={settings.business_pan} onChange={(e) => setSettings({ ...settings, business_pan: e.target.value })} /></Field>
            </div>
            <Field label="Address line 1"><Input value={settings.address1} onChange={(e) => setSettings({ ...settings, address1: e.target.value })} /></Field>
            <div className="grid grid-cols-3 gap-4">
              <Field label="City"><Input value={settings.city} onChange={(e) => setSettings({ ...settings, city: e.target.value })} /></Field>
              <Field label="State"><Input value={settings.state} onChange={(e) => setSettings({ ...settings, state: e.target.value })} /></Field>
              <Field label="PIN"><Input value={settings.pin} onChange={(e) => setSettings({ ...settings, pin: e.target.value })} /></Field>
            </div>
            {error && <div className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">{error}</div>}
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 h-11" onClick={() => setStep(1)}>Back</Button>
              <Button className="flex-1 h-11" onClick={finish} disabled={loading || !settings.trade_name} data-testid="setup-finish-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Check className="w-4 h-4" /> Finish setup</>)}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-caps">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
